import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import { Avatar as AvatarCN, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { FolderOpen, Loader2 } from 'lucide-react'
import { createProject, initializeProjects } from 'slices/projectSlice'
import { patchLocation } from 'slices/locationSlice'
import { advanceOnboardingStep, setCreatedProjectId, ONBOARDING_STEPS } from 'slices/onboardingSlice'
import { DEFAULT_PROJECT_TYPES } from 'constants/projectTypes'

interface ProjectStepProps {
	user: any
	onSkip: () => void
}

// Shared with Ask MDT's type resolver (MYD-614) through the server's copy;
// projectTypes.parity.test.ts keeps the two identical.
const PROJECT_TYPES = DEFAULT_PROJECT_TYPES

const ProjectStep: React.FC<ProjectStepProps> = ({ user, onSkip }) => {
	const dispatch = useDispatch()
	const { saving, createdLocationId } = useSelector((state: any) => state.onboarding)

	const { entities: locations, ids: locationIds } = useSelector((state: any) => state.locations)
	const firstLocation = createdLocationId
		? locations[createdLocationId]
		: locations[locationIds[0]]

	const [formData, setFormData] = useState({
		projectName: '',
		projectDescription: '',
		projectType: '',
		customProjectType: '',
	})
	const [error, setError] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target
		setFormData(prev => ({ ...prev, [name]: value }))
	}

	const isValid = formData.projectName.trim() !== '' && formData.projectType.trim() !== ''

	const handleSubmit = async () => {
		if (!isValid || !firstLocation) return

		setError('')
		setIsSubmitting(true)

		try {
			const projectType =
				formData.projectType === 'Other' && formData.customProjectType
					? formData.customProjectType
					: formData.projectType

			const result: any = await dispatch(
				createProject({
					owner: user._id,
					projectName: formData.projectName,
					projectDescription: formData.projectDescription,
					projectType,
					projectAsset: firstLocation._id,
					image: firstLocation.image,
					updateNote: 'New Project',
				}),
			)

			const projectId = result?.payload?._id
			if (projectId) {
				dispatch(setCreatedProjectId(projectId))
			}

			dispatch(initializeProjects({ locationId: firstLocation._id }))
			dispatch(
				patchLocation({
					locationId: firstLocation._id,
					updates: { updateNote: 'New Project(s) Added' },
				}),
			)

			dispatch(
				advanceOnboardingStep({
					nextStep: ONBOARDING_STEPS.TASK_TOUR,
					userId: user._id,
				}),
			)
		} catch (err) {
			setError('Failed to create project. Please try again.')
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleBack = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.LOCATION,
				userId: user._id,
			}),
		)
	}

	const handleSkipToInvite = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.INVITE,
				userId: user._id,
			}),
		)
	}

	if (!firstLocation) {
		return (
			<div className="p-6 text-center">
				<p className="text-gray-500 mb-4">
					You need to create a location first before creating a project.
				</p>
				<Button onClick={handleBack}>Go Back to Locations</Button>
			</div>
		)
	}

	return (
		<div className="p-6">
			<div className="flex items-center gap-2 mb-1">
				<FolderOpen className="w-5 h-5 text-[#0080fe]" />
				<h2 className="text-lg font-semibold text-[#074f9b]">Create Your First Project</h2>
			</div>
			<p className="text-sm text-gray-500 mb-4">
				Projects help you track deals and organize activity for a location.
			</p>

			{error && <Alert variant="danger" className="mb-4">{error}</Alert>}

			{/* Location context */}
			<div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
				<AvatarCN className="h-10 w-10">
					<AvatarImage src={firstLocation.image} />
					<AvatarFallback>{firstLocation.assetName?.charAt(0) || 'L'}</AvatarFallback>
				</AvatarCN>
				<div>
					<p className="text-sm font-medium">{firstLocation.assetName}</p>
					<p className="text-xs text-gray-500">
						{firstLocation.city}{firstLocation.stateProvince ? `, ${firstLocation.stateProvince}` : ''}
					</p>
				</div>
			</div>

			<div className="space-y-3">
				<div className="space-y-1">
					<Label htmlFor="projectName">Project Name</Label>
					<Input
						id="projectName"
						name="projectName"
						value={formData.projectName}
						onChange={handleChange}
						placeholder="Enter project name"
						className="h-9"
					/>
				</div>

				<div className="space-y-1">
					<Label htmlFor="projectType">Project Type</Label>
					<select
						id="projectType"
						name="projectType"
						value={formData.projectType}
						onChange={handleChange}
						className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						<option value="">Select a project type</option>
						{PROJECT_TYPES.map(type => (
							<option key={type} value={type}>{type}</option>
						))}
					</select>
				</div>

				{formData.projectType === 'Other' && (
					<div className="space-y-1">
						<Label htmlFor="customProjectType">Custom Type</Label>
						<Input
							id="customProjectType"
							name="customProjectType"
							value={formData.customProjectType}
							onChange={handleChange}
							placeholder="Enter custom project type"
							className="h-9"
						/>
					</div>
				)}

				<div className="space-y-1">
					<Label htmlFor="projectDescription">Description</Label>
					<textarea
						id="projectDescription"
						name="projectDescription"
						value={formData.projectDescription}
						onChange={handleChange}
						placeholder="Optional notes about this project..."
						rows={3}
						className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					/>
				</div>
			</div>

			<div className="flex gap-2 mt-6">
				<Button variant="ghost" onClick={handleBack} disabled={saving || isSubmitting} className="flex-1">
					Back
				</Button>
				<Button onClick={handleSubmit} disabled={!isValid || saving || isSubmitting} className="flex-1">
					{isSubmitting ? (
						<>
							<Loader2 className="w-4 h-4 animate-spin" />
							Creating...
						</>
					) : (
						'Create Project'
					)}
				</Button>
			</div>
			<div className="flex justify-center mt-2">
				<Button variant="ghost" onClick={handleSkipToInvite} disabled={saving} className="text-xs text-gray-400">
					Skip
				</Button>
			</div>
		</div>
	)
}

export default ProjectStep
